import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { backendURL } from '../IPaddress';
import '../CSS/CRUDfiles.css';
import {
  CrudListShell,
  ExpandableRow,
  FormGrid,
  FormActions,
  ScheduleEmptyState,
  ConfirmDeleteModal,
  FilterBar,
  SearchInput,
  SortControl,
  ResultSummary,
  useListControls,
} from '../components/crud';

const SCHOOL_TYPE_SEARCH_FIELDS = ['type', 'description'];
const SCHOOL_TYPE_SORT_OPTIONS = [
  { key: 'type', label: 'Type', accessor: (t) => t.type?.toLowerCase() ?? '' },
];


const SchoolTypeComponent = () => {
  const [types, setTypes] = useState([]);
  const [currentType, setCurrentType] = useState({ type: '', description: '' });
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const { authState } = useAuth();
  const navigate = useNavigate();
  const [showTypeNameModal, setShowTypeNameModal] = useState(false);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);

  const {
    items: visibleTypes,
    resultCount,
    totalCount,
    isFiltered,
    search,
    sort,
    clear,
  } = useListControls(types, {
    searchFields: SCHOOL_TYPE_SEARCH_FIELDS,
    sortOptions: SCHOOL_TYPE_SORT_OPTIONS,
  });




  // Authentication check
  useEffect(() => {
    if (!authState) {
        navigate('/login');
    }
  }, [authState, navigate]);

  // Fetch all types
  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    const response = await fetch(`${backendURL}/api/schooltypes`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await response.json();
    setTypes(data);
  };

  const handleFormChange = (e) => {
    setCurrentType({ ...currentType, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if type name is empty
    if (!currentType.type || currentType.type.trim() === '') {
      setShowTypeNameModal(true); // Show the modal if no type name
      return; // Stop further execution
    }

    // Check if description is empty
    if (!currentType.description || currentType.description.trim() === '') {
      setShowDescriptionModal(true); // Show the modal if no description
      return; // Stop further execution
    }

    if (currentType.type_id) {
      await updateType(currentType.type_id, currentType);
    } else {
      await createType(currentType);
    }
    setCurrentType({ type: '', description: '' });
    setShowForm(false);
    fetchTypes();
  };


  const createType = async (typeData) => {
    await fetch(`${backendURL}/api/schooltypes`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(typeData),
      credentials: 'include'
    });
  };

  const updateType = async (typeId, typeData) => {
    await fetch(`${backendURL}/api/schooltypes/${typeId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(typeData),
      credentials: 'include'
    });
  };

  const deleteType = async () => {
    if (typeToDelete) {
      await fetch(`${backendURL}/api/schooltypes/${typeToDelete}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      setTypeToDelete(null);
      setShowModal(false);
      fetchTypes();
    }
  };

  const editType = (type) => {
    setCurrentType(type);
    setShowForm(true);
  };
  const handleCancel = () => {
    setCurrentType({ type: '', description: '' });
    setShowForm(false);
  }

  const handleToggleForm = () => {
    if (!showForm) {
      setCurrentType({ type: '', description: '' });
    }
    setShowForm(!showForm);
  };

  const typeForm = (
    <Form onSubmit={handleSubmit}>
      <FormGrid>
        <Form.Group controlId="type">
          <Form.Label>Type</Form.Label>
          <Form.Control
            type="text"
            placeholder="Enter type"
            name="type"
            onChange={handleFormChange}
            value={currentType.type}
          />
        </Form.Group>
        <Form.Group controlId="description">
          <Form.Label>Description</Form.Label>
          <Form.Control
            as="textarea"
            placeholder="Enter description"
            name="description"
            onChange={handleFormChange}
            value={currentType.description}
          />
        </Form.Group>
        <FormActions>
          <Button type="submit">Submit</Button>
          <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
        </FormActions>
      </FormGrid>
    </Form>
  );

  return (
    <div>
      <CrudListShell
        addLabel="Add New Type"
        listLabel="Show Types List"
        showForm={showForm}
        onToggleForm={handleToggleForm}
        tableHeading="School Types"
        form={typeForm}
        filters={(
          <FilterBar
            summary={(
              <ResultSummary
                count={resultCount}
                total={totalCount}
                noun="school type"
                onClear={clear}
                isFiltered={isFiltered}
              />
            )}
          >
            <SearchInput label="Search school types" placeholder="Type or description" {...search} />
            <SortControl {...sort} />
          </FilterBar>
        )}
        emptyState={(
          <tr>
            <td>
              <ScheduleEmptyState>No school types have been added yet.</ScheduleEmptyState>
            </td>
          </tr>
        )}
      >
        {visibleTypes.map((type) => (
          <ExpandableRow
            key={type.type_id}
            main={{ title: type.type, subtitle: type.description || 'No description provided.' }}
            actions={(
              <>
                <Button size="sm" onClick={() => editType(type)} aria-label="Edit type">
                  <i className="bi bi-pencil"></i>
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    setTypeToDelete(type.type_id);
                    setShowModal(true);
                  }}
                  aria-label="Delete type"
                >
                  <i className="bi bi-trash3"></i>
                </Button>
              </>
            )}
          />
        ))}
      </CrudListShell>

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onConfirm={deleteType}
        bodyText="Are you sure you want to delete this type?"
      />
      <Modal show={showTypeNameModal} onHide={() => setShowTypeNameModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Missing Type Name</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please enter a name for the new school type.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTypeNameModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showDescriptionModal} onHide={() => setShowDescriptionModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Missing Description</Modal.Title>
        </Modal.Header>
        <Modal.Body>Please enter a description for the new school type.</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDescriptionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>


    </div>
  );
};

export default SchoolTypeComponent;
